import { type ReactNode } from "react";
import Done from "@mui/icons-material/Done";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import type { Theme } from "@mui/material";
import { Box, Button, Typography, useTheme } from "@mui/material";

export type ActionBannerList = "info" | "warning" | "success";

type BannerProperties = {
  borderColor: (theme: Theme) => string;
  icon: (theme: Theme) => ReactNode;
  backgroundColor?: (theme: Theme) => string;
};

const bannerTypeMapping: Record<ActionBannerList, BannerProperties> = {
  ["info"]: {
    borderColor: (theme: Theme) => theme.palette.common.lightBlue,
    icon: (theme: Theme) => (
      <InfoOutlined
        sx={{
          color: theme.palette.common.lightBlue,
          fontSize: "2rem",
        }}
      />
    ),
  },
  ["success"]: {
    borderColor: (theme: Theme) => theme.palette.success.main,
    icon: (theme: Theme) => (
      <Done
        sx={{
          color: theme.palette.success.main,
          fontSize: "2rem",
        }}
      />
    ),
  },
  ["warning"]: {
    borderColor: (theme: Theme) => theme.palette.warning.main,
    backgroundColor: (theme: Theme) => theme.palette.warning.light,
    icon: (theme: Theme) => (
      <WarningAmberOutlined
        sx={{
          color: theme.palette.warning.main,
          fontSize: "2rem",
        }}
      />
    ),
  },
};

interface ActionBannerProps {
  title: string;
  bannerType: ActionBannerList;
  onClick?: () => void;
}

const getBannerProperties = (bannerType: ActionBannerList, theme: Theme) => {
  const { borderColor, icon, backgroundColor } = bannerTypeMapping[bannerType];
  return {
    borderColor: borderColor(theme),
    icon: icon(theme),
    backgroundColor: backgroundColor ? backgroundColor(theme) : undefined,
  };
};

export function ActionBanner({
  title,
  bannerType,
  onClick,
}: ActionBannerProps) {
  const theme = useTheme();

  const { borderColor, icon, backgroundColor } = getBannerProperties(
    bannerType,
    theme,
  );

  return (
    <Box
      sx={{
        border: `1px solid ${borderColor}`,
        backgroundColor: backgroundColor ?? "transparent",
      }}
    >
      <Box
        sx={{
          padding: "1rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>{icon}</Box>
        <Typography>{title}</Typography>
        {onClick && (
          <Button
            data-testid="action-link"
            onClick={onClick}
            variant="contained"
          >
            View
          </Button>
        )}
      </Box>
    </Box>
  );
}
